import { useState, useCallback, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  MessageCircle,
  Users,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
  Lightbulb,
  BookOpen,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import {
  getChatbotStats,
  getChatbotSessions,
  getChatbotSessionMessages,
  getChatbotTopQuestions,
  generateChatbotInsights,
  type ChatbotStats,
  type ChatSessionSummary,
  type ChatMessageEntry,
  type TopQuestion,
  type ChatbotInsights,
} from '../../../lib/api'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import StatCard from '../../../components/analytics/StatCard'
import PeriodSelector from '../../../components/analytics/PeriodSelector'
import { cn } from '../../../lib/utils'

const NAV_TABS = [
  { path: '/admin/analytics', label: 'Overview' },
  { path: '/admin/analytics/users', label: 'Users' },
  { path: '/admin/analytics/apps', label: 'Apps' },
  { path: '/admin/analytics/activity', label: 'Activity' },
  { path: '/admin/analytics/features', label: 'Features' },
  { path: '/admin/analytics/system', label: 'System' },
  { path: '/admin/analytics/chatbot', label: 'Chatbot' },
]

const SESSIONS_PER_PAGE = 10

export default function ChatbotAnalytics() {
  const location = useLocation()
  const [period, setPeriod] = useState('30d')
  const [stats, setStats] = useState<ChatbotStats | null>(null)
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([])
  const [sessionsPage, setSessionsPage] = useState(1)
  const [hasMoreSessions, setHasMoreSessions] = useState(true)
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false)
  const [topQuestions, setTopQuestions] = useState<TopQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [escalatedOnly, setEscalatedOnly] = useState(false)
  const [resolutionFilter, setResolutionFilter] = useState<'all' | 'solved' | 'unresolved'>('all')
  const [expandedSession, setExpandedSession] = useState<number | null>(null)
  const [sessionMessages, setSessionMessages] = useState<Record<number, ChatMessageEntry[]>>({})
  const [loadingMessages, setLoadingMessages] = useState<number | null>(null)

  // Insights modal state
  const [insightsOpen, setInsightsOpen] = useState(false)
  const [insights, setInsights] = useState<ChatbotInsights | null>(null)
  const [insightsLoading, setInsightsLoading] = useState(false)
  const [insightsError, setInsightsError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      const [statsData, sessionsData, questionsData] = await Promise.all([
        getChatbotStats(period),
        getChatbotSessions(period, 1, escalatedOnly, SESSIONS_PER_PAGE, resolutionFilter === 'all' ? undefined : resolutionFilter),
        getChatbotTopQuestions(period),
      ])
      setStats(statsData)
      setSessions(sessionsData)
      setSessionsPage(1)
      setHasMoreSessions(sessionsData.length >= SESSIONS_PER_PAGE)
      setTopQuestions(questionsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chatbot analytics')
      throw err
    } finally {
      setLoading(false)
    }
  }, [period, escalatedOnly, resolutionFilter])

  useEffect(() => {
    setLoading(true)
    fetchData()
  }, [fetchData])

  useAutoRefresh(fetchData, {
    baseInterval: 60000,
    maxInterval: 300000,
  })

  async function handleLoadMoreSessions() {
    const nextPage = sessionsPage + 1
    setLoadingMoreSessions(true)
    try {
      const more = await getChatbotSessions(period, nextPage, escalatedOnly, SESSIONS_PER_PAGE, resolutionFilter === 'all' ? undefined : resolutionFilter)
      setSessions((prev) => [...prev, ...more])
      setSessionsPage(nextPage)
      setHasMoreSessions(more.length >= SESSIONS_PER_PAGE)
    } catch {
      // silently fail
    } finally {
      setLoadingMoreSessions(false)
    }
  }

  async function handleToggleSession(sessionId: number) {
    if (expandedSession === sessionId) {
      setExpandedSession(null)
      return
    }
    setExpandedSession(sessionId)
    if (!sessionMessages[sessionId]) {
      setLoadingMessages(sessionId)
      try {
        const msgs = await getChatbotSessionMessages(sessionId)
        setSessionMessages((prev) => ({ ...prev, [sessionId]: msgs }))
      } catch {
        // silently fail
      } finally {
        setLoadingMessages(null)
      }
    }
  }

  async function handleGenerateInsights() {
    setInsightsOpen(true)
    setInsightsLoading(true)
    setInsightsError(null)
    try {
      const data = await generateChatbotInsights(period)
      setInsights(data)
    } catch (err) {
      setInsightsError(err instanceof Error ? err.message : 'Failed to generate insights')
    } finally {
      setInsightsLoading(false)
    }
  }

  function formatDate(iso: string): string {
    const d = new Date(iso)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  function formatTime(iso: string): string {
    const d = new Date(iso)
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Chatbot Analytics</h1>
          <p className="text-muted-foreground">
            Usage, common questions, and escalation patterns
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Nav tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-border">
        {NAV_TABS.map((tab) => (
          <Link
            key={tab.path}
            to={tab.path}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              location.pathname === tab.path
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            )}
          >
            {tab.label}
          </Link>
        ))}
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {loading && !stats ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : !stats ? (
        <div className="text-center py-12 text-muted-foreground">
          No chatbot data yet. Conversations will appear here once users start chatting.
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <StatCard
              label="Conversations"
              value={stats.total_conversations}
              icon={MessageCircle}
            />
            <StatCard
              label="Total Messages"
              value={stats.total_messages}
              icon={MessageCircle}
            />
            <StatCard
              label="Unique Users"
              value={stats.unique_users}
              icon={Users}
            />
            <StatCard
              label="Avg Messages / Conversation"
              value={stats.avg_messages_per_conversation}
              icon={TrendingUp}
            />
            <StatCard
              label="Escalations"
              value={stats.escalation_count}
              icon={AlertTriangle}
            />
            <StatCard
              label="Escalation Rate"
              value={`${stats.escalation_rate}%`}
              icon={AlertTriangle}
            />
            <StatCard
              label="Resolved"
              value={stats.resolved_count}
              icon={CheckCircle2}
            />
            <StatCard
              label="Resolution Rate"
              value={`${stats.resolution_rate}%`}
              icon={CheckCircle2}
            />
          </div>
          <p className="mb-8 text-sm text-muted-foreground">
            <strong>Escalations</strong> are conversations where the chatbot could not help and
            offered to connect the user with the team or report a bug. <strong>Resolved</strong>{' '}
            counts conversations the user marked as &quot;Yes, problem solved&quot; on the last
            answer. Resolution rate is computed only over sessions where the user gave feedback.
          </p>

          {/* Two-column layout: Questions + Sessions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Questions */}
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Recent Questions</h3>
                <button
                  onClick={handleGenerateInsights}
                  disabled={topQuestions.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Generate Insights
                </button>
              </div>
              {topQuestions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No questions yet</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {topQuestions.map((q, i) => (
                    <div
                      key={i}
                      className={cn(
                        'p-3 rounded-lg border text-sm',
                        q.escalated
                          ? 'border-amber-500/30 bg-amber-500/5'
                          : 'border-border bg-secondary/30'
                      )}
                    >
                      <p className="font-medium mb-1 line-clamp-2">{q.question}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{q.user_email}</span>
                        <span>{formatDate(q.date)}</span>
                        <span>{q.message_count} msgs</span>
                        {q.escalated && (
                          <span className="text-amber-600 font-medium">Escalated</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Conversations */}
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h3 className="text-lg font-semibold">Conversations</h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={escalatedOnly}
                      onChange={(e) => setEscalatedOnly(e.target.checked)}
                      className="rounded border-border"
                    />
                    Escalated only
                  </label>
                  <select
                    value={resolutionFilter}
                    onChange={(e) => setResolutionFilter(e.target.value as 'all' | 'solved' | 'unresolved')}
                    className="text-sm bg-secondary/50 border border-border rounded-md px-2 py-1"
                  >
                    <option value="all">All outcomes</option>
                    <option value="solved">Solved</option>
                    <option value="unresolved">Unresolved</option>
                  </select>
                </div>
              </div>
              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No conversations found</p>
              ) : (
                <>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {sessions.map((s) => (
                      <div key={s.id} className="border border-border rounded-lg">
                        <button
                          onClick={() => handleToggleSession(s.id)}
                          className="w-full flex items-center gap-3 p-3 text-left hover:bg-secondary/50 transition-colors"
                        >
                          {expandedSession === s.id ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium truncate">{s.user_email}</span>
                              {s.escalated && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 font-medium flex-shrink-0">
                                  Escalated
                                </span>
                              )}
                              {s.resolution === 'solved' && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-600 dark:text-green-400 font-medium flex-shrink-0">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Solved
                                </span>
                              )}
                              {s.resolution === 'unresolved' && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 font-medium flex-shrink-0">
                                  <XCircle className="w-3 h-3" />
                                  Unresolved
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {formatDate(s.started_at)} at {formatTime(s.started_at)} — {s.message_count} messages
                            </div>
                          </div>
                        </button>

                        {/* Expanded messages */}
                        {expandedSession === s.id && (
                          <div className="border-t border-border p-3 space-y-2 bg-secondary/20">
                            {loadingMessages === s.id ? (
                              <div className="flex justify-center py-4">
                                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                              </div>
                            ) : sessionMessages[s.id] ? (
                              sessionMessages[s.id].map((msg, i) => (
                                <div
                                  key={i}
                                  className={cn(
                                    'text-sm p-2 rounded',
                                    msg.role === 'user'
                                      ? 'bg-primary/10 ml-4'
                                      : 'bg-card mr-4'
                                  )}
                                >
                                  <div className="text-[10px] text-muted-foreground mb-1 font-medium uppercase">
                                    {msg.role}
                                  </div>
                                  <p className="whitespace-pre-wrap text-xs leading-relaxed">
                                    {msg.content.replace(/\[NEED_MORE_HELP\]/g, '').replace(/\[FEEDBACK:[^\]]*\]/g, '').trim()}
                                  </p>
                                </div>
                              ))
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Load more */}
                  {hasMoreSessions && (
                    <div className="mt-3 text-center">
                      <button
                        onClick={handleLoadMoreSessions}
                        disabled={loadingMoreSessions}
                        className="text-sm text-primary hover:underline disabled:opacity-50"
                      >
                        {loadingMoreSessions ? (
                          <span className="flex items-center gap-1.5 justify-center">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Loading...
                          </span>
                        ) : (
                          'Load more conversations'
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Insights Modal */}
      {insightsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setInsightsOpen(false)}
          />
          {/* Modal */}
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">AI-Generated Insights</h2>
              </div>
              <button
                onClick={() => setInsightsOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {insightsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Analyzing conversations with Gemini...
                  </p>
                </div>
              ) : insightsError ? (
                <div className="p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 text-sm">
                  {insightsError}
                </div>
              ) : insights ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                    <p className="text-sm leading-relaxed">{insights.summary}</p>
                  </div>

                  {/* Themes */}
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Recurring Themes
                    </h3>
                    <div className="space-y-3">
                      {insights.themes.map((theme, i) => (
                        <div key={i} className="p-4 rounded-lg border border-border bg-secondary/20">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h4 className="font-semibold text-sm">{theme.name}</h4>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                                {theme.count} questions
                              </span>
                              {theme.escalation_rate > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-medium">
                                  {theme.escalation_rate}% escalated
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{theme.description}</p>
                          <div className="text-xs bg-card rounded p-2 border border-border mb-2 italic text-muted-foreground">
                            "{theme.example_question}"
                          </div>
                          <div className="flex items-start gap-1.5 text-xs text-primary">
                            <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>{theme.recommendation}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Knowledge Gaps */}
                  {insights.gaps && insights.gaps.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Knowledge Gaps
                      </h3>
                      <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
                        <p className="text-xs text-muted-foreground mb-2">
                          Topics the chatbot struggled with that should be added to its knowledge base:
                        </p>
                        <ul className="space-y-1.5">
                          {insights.gaps.map((gap, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <BookOpen className="w-3.5 h-3.5 mt-0.5 text-amber-600 flex-shrink-0" />
                              <span>{gap}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
